import { useEffect } from 'react';
import { useFramework } from '@/framework';
import type { MouseOperate } from '@/plugins/mouse-operate';
import { useReferenceLineSnap } from './use-reference-line-snap';

/** Scale 参考线吸附实现保留，但交互继续保持关闭。 */
const ENABLE_SCALE_SNAP = false;

export const useReferenceLinesOperationEvents = () => {
    const framework = useFramework();
    const snap = useReferenceLineSnap();
    const mouseOperate = framework.service.registration.getInstance<MouseOperate>('MouseOperate');

    useEffect(() => {
        if (!mouseOperate) return;

        const cleanups = [
            mouseOperate.onMoveStart(snap.prepare),
            mouseOperate.onMoveBeforeUpdate((event) => {
                if (!event.layer) return;
                const result = snap.move({
                    rawX: event.rawX,
                    rawY: event.rawY,
                    oldLayer: event.oldLayer,
                    currentLayer: event.layer,
                });
                if (result.isSnapX) {
                    event.candidates.add({
                        slot: 'move-x',
                        value: result.x,
                        correction: result.x - event.rawX,
                        source: 'reference-lines',
                    });
                }
                if (result.isSnapY) {
                    event.candidates.add({
                        slot: 'move-y',
                        value: result.y,
                        correction: result.y - event.rawY,
                        source: 'reference-lines',
                    });
                }
            }),
            mouseOperate.onMoveAfterUpdate((event) => {
                if (event.layer) snap.updateHighlight(event.layer);
            }),
            mouseOperate.onMoveEnd(snap.reset),
            mouseOperate.onResizeStart(snap.prepare),
            mouseOperate.onResizeBeforeUpdate((event) => {
                if (!event.layer) return;
                const result = snap.resize({
                    rawDisplacementX: event.rawDisplacementX,
                    rawDisplacementY: event.rawDisplacementY,
                    oldLayer: event.oldLayer,
                    currentLayer: event.layer,
                    operateType: event.operateType,
                    direction: event.direction,
                });
                if (!result.isSnap) return;

                const value = (event.isChangeHeight ? result.y : result.x) * event.direction;
                event.candidates.add({
                    slot: 'resize-offset',
                    value,
                    correction: value - event.rawOffset,
                    source: 'reference-lines',
                });
            }),
            mouseOperate.onResizeAfterUpdate((event) => {
                if (event.layer) snap.updateHighlight(event.layer);
            }),
            mouseOperate.onResizeEnd(snap.reset),
            mouseOperate.onCancel(snap.reset),
        ];

        if (ENABLE_SCALE_SNAP) {
            cleanups.push(mouseOperate.onScaleStart(snap.prepare));
        }

        return () => cleanups.forEach((cleanup) => cleanup());
        // 吸附控制器的内部状态都保存在 ref 中，只在插件挂载时订阅一次。
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
};
